import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../../../../../contexts/AuthContext";
import {
  assignLincCoach,
  checkAccessLincOptions,
  checkLincBookingStatus,
  fetchAssessmentFile,
  fetchLincCoachesAndGuides,
  requestExternalAssessment,
} from "../../../../../../services/users.service";
import { getLinc } from "../../../../../../services/legal.service";
import Button from "../../../../../../components/button";
import {
  ButtonSize,
  ButtonVariant,
} from "../../../../../../components/button/types";
import { MyAreaTitle } from "../../components";
import { CoachSelectionView } from "../CVCheck/CVCheckPopup/pages/Components/CoachSelectionView";
import { CircularImage } from "../../../../../../components/circularImage";
import { BusinessInfoContent } from "../../../../../../@types";
import { ReactComponent as CloseIcon } from "../../../../../../assets/close.svg";
import lincAnimation from "../../../../../../assets/linc-animation.json";
import Lottie from "lottie-react";

// --- Types ---
export interface AssessmentStatus {
  personalityFileAvailable: boolean;
  careerFileAvailable: boolean;
}

export interface LincCoach {
  id: number;
  name: string;
  platformContentId: string;
  profilePicture?: string;
}

type ModalStep = "initial" | "coachSelection" | "confirmation";
type ConfirmationType = "withCoaching" | "withoutCoaching";

// --- Helper Functions ---
const ensureProtocol = (url?: string): string | undefined => {
  if (!url) return undefined;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("//")) return `https:${url}`;
  if (url.includes(".") && !url.startsWith("/")) return `https://${url}`;
  return undefined;
};

// --- Component ---
export function SterngthAndPersonalityProfile() {
  const { t } = useTranslation("myArea");
  const { user } = useContext(AuthContext);
  const queryClient = useQueryClient();

  // --- State ---
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState<ModalStep>("initial");
  const [selectedCoach, setSelectedCoach] = useState<LincCoach | null>(null);
  const [confirmationType, setConfirmationType] =
    useState<ConfirmationType | null>(null);
  const [hasCompletedBookingFlow, setHasCompletedBookingFlow] = useState(false);
  const [content, setContent] = useState<BusinessInfoContent | null>(null);
  const [assignmentError, setAssignmentError] = useState<string | null>(null);

  // --- Queries ---
  const { data: assessmentStatus } = useQuery<AssessmentStatus>(
    "checkAccessLincOptions",
    checkAccessLincOptions
  );

  const {
    data: availableCoaches,
    isLoading: isLoadingCoaches,
    error: coachesError,
  } = useQuery<LincCoach[]>("availableLincCoaches", fetchLincCoachesAndGuides, {
    enabled: isModalOpen,
    staleTime: 5 * 60 * 1000,
    onSuccess: (data) => {
      if (
        modalStep === "initial" &&
        data &&
        data.length > 0 &&
        !selectedCoach
      ) {
        setSelectedCoach(data[0]);
      } else if (modalStep === "initial" && (!data || data.length === 0)) {
        setSelectedCoach(null);
      }
    },
  });

  // --- Mutations ---
  const fetchAssessmentFileMutation = useMutation(fetchAssessmentFile, {
    onError: (error) => console.error("Error downloading file:", error),
    onSuccess: (data) => {
      if (data && typeof data === "string") {
        window.open(data, "_blank");
      } else {
        console.error("Invalid URL received for file download");
      }
    },
  });

  const assignCoachMutation = useMutation(assignLincCoach, {
    onSuccess: () => {
      console.log("Coach assignment successful via mutation.");
      // queryClient.invalidateQueries('someRelatedQuery');
    },
    onError: (error: any) => {
      console.error("Failed to assign coach via mutation:", error);
      const errorMessage =
        error?.message || t("assessments.errors.coachAssignmentFailed");
      setAssignmentError(errorMessage);
    },
  });

  const sendLINCMutation = useMutation(requestExternalAssessment, {
    onSuccess: () => {
      console.log("linc sent successful via mutation.");
    },
    onError: (error: any) => {},
  });
  // --- Derived State ---
  const assessmentAvailable = user?.externalAssessment?.link;

  const { data: initialBookingStatus, isLoading: isLoadingBookingStatus } =
    useQuery<boolean>("lincBookingStatus", checkLincBookingStatus, {
      enabled: !!assessmentAvailable,
    });

  const hasResults =
    assessmentStatus?.personalityFileAvailable ||
    assessmentStatus?.careerFileAvailable;
  const lincUrl = assessmentAvailable ?? "#";

  // --- Event Handlers ---
  const handleOpenModal = useCallback(() => {
    if (!assessmentAvailable) {
      console.warn("Assessment link not available.");
      return;
    }
    setAssignmentError(null);
    setModalStep("initial");
    setSelectedCoach(null);
    setIsModalOpen(true);
  }, [assessmentAvailable]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setModalStep("initial");
    setAssignmentError(null);
  }, []);

  const handleNavigateToCoachSelection = useCallback(() => {
    setAssignmentError(null);
    setModalStep("coachSelection");
  }, []);

  const handleCoachSelected = useCallback((coach: LincCoach) => {
    setSelectedCoach(coach);
    setModalStep("initial");
    setAssignmentError(null);
  }, []);

  const handleBackFromCoachSelection = useCallback(() => {
    setModalStep("initial");
  }, []);

  const handleNavigateToConfirmation = useCallback((type: ConfirmationType) => {
    if (type === "withCoaching") sendLINCMutation.mutate(); // TODO replace with proper function once we have the template
    if (type === "withoutCoaching") sendLINCMutation.mutate();
    setConfirmationType(type);
    setModalStep("confirmation");
  }, []);

  const handleAssignCoachAndProceed = useCallback(async () => {
    const coachToAssign =
      selectedCoach ??
      (availableCoaches && availableCoaches.length > 0
        ? availableCoaches[0]
        : null);

    if (!coachToAssign) {
      console.warn(
        "Attempted to proceed with coaching, but no coach is selected or available."
      );
      setAssignmentError(t("assessments.errors.noCoachSelectedError"));
      return;
    }

    if (assignCoachMutation.isLoading) {
      return;
    }

    setAssignmentError(null);

    try {
      const payload = {
        platformContentId: coachToAssign.platformContentId,
      };

      await assignCoachMutation.mutateAsync(payload);
      console.log("Assignment successful, navigating to confirmation.");
      handleNavigateToConfirmation("withCoaching");
    } catch (error) {
      console.error("Coach assignment failed in handleAssignCoachAndProceed.");
    }
  }, [
    selectedCoach,
    availableCoaches,
    assignCoachMutation,
    handleNavigateToConfirmation,
    t,
  ]);

  const handleStartAssessmentAndClose = useCallback(() => {
    setHasCompletedBookingFlow(true);
    window.open(lincUrl, "_blank");
    handleCloseModal();
  }, [lincUrl, handleCloseModal, queryClient]);

  const handleMainAssessmentAction = useCallback(() => {
    if (hasCompletedBookingFlow) {
      console.log("Booking flow previously completed, opening LINC directly.");
      window.open(lincUrl, "_blank");
    } else {
      handleOpenModal();
    }
  }, [hasCompletedBookingFlow, lincUrl, handleOpenModal]);

  const handleDownloadClick = useCallback(
    (type: "personality" | "career") => {
      fetchAssessmentFileMutation.mutate({ type });
    },
    [fetchAssessmentFileMutation]
  );

  // Memoize props for CoachSelectionView
  const coachSelectionProps = useMemo(
    () => ({
      coaches: availableCoaches ?? [],
      selectedCoach: selectedCoach,
      isLoading: isLoadingCoaches,
      error: coachesError,
      onSelectCoach: handleCoachSelected,
      onBack: handleBackFromCoachSelection,
    }),
    [
      availableCoaches,
      selectedCoach,
      isLoadingCoaches,
      coachesError,
      handleCoachSelected,
      handleBackFromCoachSelection,
    ]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getLinc();
        if (res?.renderedContent) {
          setContent(res);
        }
      } catch (error) {
        console.error("Failed to fetch LINC content:", error);
      }
    };
    fetchData();
  }, []);

  const hasEnoughCredits = useCallback(() => {
    if (user) {
      if (user.availableCredits >= 4) return true;
    }
    return false;
  }, [user]);

  useEffect(() => {
    if (typeof initialBookingStatus === "boolean") {
      if (hasCompletedBookingFlow !== initialBookingStatus) {
        setHasCompletedBookingFlow(initialBookingStatus);
      }
    }
  }, [initialBookingStatus, hasCompletedBookingFlow]);

  // --- Render Logic ---
  const renderModalContent = () => {
    switch (modalStep) {
      // ---- STEP 1: Initial View (Show coach, options to proceed/change) ----
      case "initial":
        const defaultCoach =
          selectedCoach ??
          (availableCoaches && availableCoaches.length > 0
            ? availableCoaches[0]
            : null);
        const canChangeCoach = availableCoaches && availableCoaches.length > 1;
        const profilePicUrl = ensureProtocol(defaultCoach?.profilePicture);

        return (
          <>
            {/* Coach Display Area */}
            <div className="mb-6">
              <p className="mb-6">
                {t("assessments.bookingDialog.initial-description")}
              </p>

              <div className="flex justify-between items-center mb-2">
                <span className="text-base font-semibold text-gray-700">
                  {t("assessments.bookingDialog.yourCoachLabel")}:
                </span>
              </div>

              {/* Coach Display */}
              {!isLoadingCoaches && !coachesError && defaultCoach && (
                <div className="flex p-3  rounded-lg  bg-Yellow-super-light">
                  <div className="w-12 h-12 mr-4 overflow-hidden rounded-full flex-shrink-0  bg-gray-100 flex items-center justify-center">
                    {" "}
                    {profilePicUrl ? (
                      <CircularImage
                        src={profilePicUrl}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-gray-500 text-lg font-semibold">
                        {defaultCoach.name?.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col flex-grow">
                    <div className="flex justify-between items-start space-x-2">
                      {/* Coach name */}
                      <span className="font-bold text-sm ">
                        {defaultCoach.name}
                      </span>

                      {/* Change coach button - top right */}
                      {canChangeCoach && !assignCoachMutation.isLoading && (
                        <button
                          onClick={handleNavigateToCoachSelection}
                          className="text-sm underline text-[#757575] font-semibold hover:text-gray-600 focus:outline-none"
                        >
                          {t("assessments.bookingDialog.changeCoach")}
                        </button>
                      )}
                    </div>

                    {/* Certification - below coach name */}
                    <span className="font-medium text-sm text-gray-800 mt-1">
                      {t("assessments.bookingDialog.certified")}
                    </span>
                  </div>
                </div>
              )}

              {/* No Coaches Available Message */}
              {!isLoadingCoaches && !coachesError && !defaultCoach && (
                <div className="p-4 bg-yellow-50 text-yellow-800 rounded-lg border border-yellow-200 text-sm">
                  {t("assessments.bookingDialog.noCoachesProceedInfo")}
                </div>
              )}

              {/* Assignment Error Message */}
              {assignmentError && (
                <p className="mt-3 text-sm text-red-600 text-center">
                  {assignmentError}
                </p>
              )}
            </div>

            {/* Footer for Initial Step */}
            <div
              className="w-full py-3 px-4 lg:py-0 lg:px-0
  fixed bottom-0 left-0 bg-white 
  lg:relative lg:bottom-auto lg:left-auto lg:bg-transparent"
            >
              <div className="flex justify-between items-center  space-x-2  mt-auto">
                <button
                  onClick={() =>
                    handleNavigateToConfirmation("withoutCoaching")
                  }
                  className="text-sm underline text-gray-600 hover:text-gray-800 font-medium focus:outline-none"
                  disabled={assignCoachMutation.isLoading}
                >
                  {t("assessments.bookingDialog.proceedWithout")}
                </button>
                <Button
                  variant={ButtonVariant.PRIMARY}
                  size={ButtonSize.SMALL}
                  onClick={handleAssignCoachAndProceed}
                  disabled={
                    isLoadingCoaches ||
                    !defaultCoach ||
                    assignCoachMutation.isLoading ||
                    !hasEnoughCredits()
                  }
                >
                  {hasEnoughCredits()
                    ? t("assessments.bookingDialog.proceedWith")
                    : t("assessments.bookingDialog.notEnoughCredits")}
                </Button>
              </div>
            </div>
          </>
        );

      // ---- STEP 2: Coach Selection ----
      case "coachSelection":
        return (
          <>
            <p className="">
              {t("assessments.bookingDialog.selectCoachDescription")}
            </p>
            <CoachSelectionView {...coachSelectionProps} />
          </>
        );

      // ---- STEP 3: Confirmation ----
      case "confirmation":
        const isWithCoaching = confirmationType === "withCoaching";

        return (
          <div className="flex flex-col items-center text-center">
            {/* Animation Placeholder */}

            {/* Description */}
            <p className=" mb-6 mt-4">
              {isWithCoaching
                ? t("assessments.confirmation.withCoaching.success")
                : t("assessments.confirmation.withoutCoaching.success")}
            </p>
            {isWithCoaching ? (
              <div className="flex flex-col items-center justify-center my-4">
                <div className="w-40 h-40">
                  <Lottie
                    animationData={lincAnimation}
                    loop={false}
                    style={{ width: "146px", height: "146px" }}
                  />
                </div>

                <p className="mt-4">
                  {t("assessments.confirmation.withCoaching.info")}
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center my-4">
                <p className="mt-4 ">
                  {t("assessments.confirmation.withoutCoaching.info")}
                </p>
              </div>
            )}

            {/* Footer for Confirmation Step */}
            <div
              className="w-full py-3 px-4 lg:py-0 lg:px-0 
  fixed bottom-0 left-0 bg-white 
  lg:relative lg:bottom-auto lg:left-auto lg:bg-transparent"
            >
              <div className="w-full flex justify-center pt-4  mt-auto">
                <Button
                  variant={ButtonVariant.PRIMARY}
                  size={ButtonSize.MEDIUM}
                  onClick={handleStartAssessmentAndClose}
                >
                  {t("assessments.confirmation.accessLincButton")}
                </Button>
              </div>
            </div>
          </div>
        );

      default:
        return null; // Should not happen
    }
  };

  // --- Main Component Return ---
  return (
    <div>
      {/* --- Page Content (Assessment Info) --- */}
      <div className="hidden lg:flex">
        <MyAreaTitle title={t("assessments.title-strength")} />
      </div>
      <h1 className="text-Grey-dark text-xl font-black mb-6 ">
        {content?.title ?? t("assessments.title-strength")}
      </h1>
      <div>
        <div
          className=""
          dangerouslySetInnerHTML={{ __html: content?.renderedContent || "" }}
        />
        <div className="flex items-center justify-between mt-4 mb-2">
          <span className="text-Grey-dark text-sm">
            {t("assessments.strength-finder.credits")}: 0 (
            {t("assessments.strength-finder.inclusive")})
          </span>

          {/* --- Main Button to Start Flow or Access LINC --- */}
          {!hasResults && (
            <Button
              onClick={handleMainAssessmentAction}
              variant={ButtonVariant.SECONDARY}
              size={ButtonSize.SMALL}
              disabled={!assessmentAvailable}
            >
              {!assessmentAvailable
                ? t("assessments.strength-finder.button-text-unavailable")
                : hasCompletedBookingFlow
                ? t("assessments.strength-finder.button-text-access")
                : t("assessments.strength-finder.button-text-start")}
            </Button>
          )}

          {/* --- Download Buttons (If Results Available) --- */}
          {hasResults && (
            <div className="flex gap-4">
              {assessmentStatus?.personalityFileAvailable && (
                <Button
                  onClick={() => handleDownloadClick("personality")}
                  variant={ButtonVariant.PRIMARY}
                  size={ButtonSize.SMALL}
                >
                  {t("assessments.strength-finder.download-personality")}
                </Button>
              )}
              {assessmentStatus?.careerFileAvailable && (
                <Button
                  onClick={() => handleDownloadClick("career")}
                  variant={ButtonVariant.PRIMARY}
                  size={ButtonSize.SMALL}
                >
                  {t("assessments.strength-finder.download-career")}
                </Button>
              )}
            </div>
          )}
        </div>
        {/* Availability Message */}
        {!hasResults && !assessmentAvailable ? (
          <span className="text-Grey-dark text-xs italic">
            *{t("assessments.strength-finder.not-available-message")}
          </span>
        ) : null}
      </div>

      {/* --- Modal Structure --- */}
      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
          <div className="bg-white p-6 lg:rounded-lg shadow-lg lg:w-1/3 w-full h-full lg:h-auto ">
            {" "}
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-800">
                {/* Dynamic Title */}
                {modalStep === "initial" &&
                  t("assessments.bookingDialog.title")}
                {modalStep === "coachSelection" &&
                  t("assessments.bookingDialog.selectCoachTitle")}
                {modalStep === "confirmation" &&
                  t("assessments.confirmation.withCoaching.title")}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <CloseIcon />
              </button>
            </div>
            {/* Body - Renders content based on modalStep */}
            <div className="flex-grow flex flex-col overflow-y-auto pr-1 ">
              {/* Added flex flex-col to help footer stick to bottom if content is short */}
              {renderModalContent()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
